// Edit an order from the WMS (Fernando, 2026-10-05: "a Nora Mikken customer selected XL but wanted L").
// Per line: another size of the same article (the product family, GET /api/returns/product-family), ANOTHER
// product of the store (search, GET /api/returns/product-search), fewer units, or 0 to remove it — no new lines,
// no extra units (Fernando, 5.10). The price paid is kept; what comes off is refunded to match. Preview first — on Shopify that stages the edit and reads the shop's own totals
// without changing anything — then "Change the order in the shop". The shop does not e-mail the customer;
// a swap keeps the price paid. Backend: main.py order_edit_* + order_edit.py; docs/ORDER-EDIT.md.
import React, { useEffect, useState } from 'react';
import { authFetch, API } from './api';
import { titleEn, customerWords } from './lineName';

interface Line {
  line_id: string; variation_id: string; product_code: string; product_name: string; model_name: string;
  quantity: number; paid_unit: number;
}
interface Opts {
  editable: boolean; reasons: string[]; warnings: string[]; platform: string; webshop_id: string; currency: string;
  can_swap: boolean; cant_swap_why?: string | null; lines: Line[];
}
interface Fam { variation_id: string; product_code: string; product_name?: string; size?: string; stock_qty?: number | null; is_current?: boolean }

export default function OrderEditDialog({ orderId, orderNumber, onClose, onDone }: {
  orderId: string; orderNumber: string; onClose: () => void; onDone: (summary: string) => void;
}) {
  const [opts, setOpts] = useState<Opts | null>(null);
  const [err, setErr] = useState('');
  const [qty, setQty] = useState<Record<string, number>>({});
  const [to, setTo] = useState<Record<string, string>>({});
  const [fam, setFam] = useState<Record<string, Fam[]>>({});
  const [preview, setPreview] = useState<any | null>(null);
  const [busy, setBusy] = useState(false);
  // "Other product…": a search per line; the chosen product's label shown in the size column
  const [searchFor, setSearchFor] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<Fam[]>([]);
  const [chosen, setChosen] = useState<Record<string, string>>({});
  useEffect(() => {
    const t = setTimeout(async () => {
      if (!searchFor || q.trim().length < 2 || !opts) { setHits([]); return; }
      const r = await authFetch(`${API}/returns/product-search?q=${encodeURIComponent(q.trim())}&webshop_id=${encodeURIComponent(opts.webshop_id)}`);
      if (r.ok) setHits((await r.json()).results || []);
    }, 300);
    return () => clearTimeout(t);
  }, [q, searchFor, opts]);

  useEffect(() => {
    (async () => {
      const r = await authFetch(`${API}/orders/${encodeURIComponent(orderId)}/edit/options`);
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(typeof d.detail === 'string' ? d.detail : 'Could not load the order'); return; }
      setOpts(d);
      const q: Record<string, number> = {};
      (d.lines || []).forEach((l: Line) => { q[l.line_id] = l.quantity; });
      setQty(q);
    })();
  }, [orderId]);

  const loadFamily = async (l: Line) => {
    if (fam[l.line_id] || !opts) return;
    const r = await authFetch(`${API}/returns/product-family?variation_id=${encodeURIComponent(l.variation_id)}&webshop_id=${encodeURIComponent(opts.webshop_id)}`);
    const d = await r.json().catch(() => ({}));
    setFam(f => ({ ...f, [l.line_id]: d.results || [] }));
  };

  const changes = (opts?.lines || [])
    .filter(l => (qty[l.line_id] ?? l.quantity) !== l.quantity || (to[l.line_id] && to[l.line_id] !== l.variation_id))
    .map(l => ({ line_id: l.line_id, quantity: qty[l.line_id] ?? l.quantity, to_variation_id: to[l.line_id] || null }));
  const money = (v: number) => `${v.toFixed(2)} ${opts?.currency || ''}`;

  const doPreview = async () => {
    setBusy(true); setErr(''); setPreview(null);
    const r = await authFetch(`${API}/orders/${encodeURIComponent(orderId)}/edit/preview`, { method: 'POST', body: JSON.stringify({ changes }) });
    const d = await r.json().catch(() => ({}));
    setBusy(false);
    if (!r.ok) { setErr(typeof d.detail === 'string' ? d.detail : (d.detail?.reasons || []).join('; ') || 'Preview failed'); return; }
    setPreview(d);
  };
  const doCommit = async () => {
    setBusy(true); setErr('');
    const r = await authFetch(`${API}/orders/${encodeURIComponent(orderId)}/edit`, { method: 'POST', body: JSON.stringify({ changes, confirm: true }) });
    const d = await r.json().catch(() => ({}));
    setBusy(false);
    if (!r.ok) { setErr(typeof d.detail === 'string' ? d.detail : (d.detail?.reasons || []).join('; ') || 'The change failed'); return; }
    onDone(`Order changed in the shop: ${(d.changes || []).join('; ')}${d.label ? ` · ${d.label}` : ''}`);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-2 md:p-6" onClick={() => { if (!busy) onClose(); }}>
      <div className="bg-[#1a1d27] border border-gray-800 rounded-xl p-5 w-full max-w-2xl space-y-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-white text-base font-semibold">Edit order {orderNumber}</h3>
            <p className="text-gray-500 text-xs mt-0.5">Change a line to another size or product, or remove it — in the shop itself; the customer gets no e-mail.
              The price paid is kept (a dearer item shows its own price with a discount); what comes off is refunded.</p>
          </div>
          <button onClick={onClose} disabled={busy} className="text-gray-500 hover:text-white">✕</button>
        </div>
        {!opts && !err && <div className="text-gray-400 text-sm">Loading…</div>}
        {opts && !opts.editable && (
          <div className="rounded-lg border border-amber-800/60 bg-amber-950/30 px-3 py-2 text-sm text-amber-200 space-y-1">
            <div className="font-semibold">This order can't be changed from the WMS:</div>
            {opts.reasons.map((r, i) => <div key={i}>• {r}</div>)}
          </div>
        )}
        {opts && opts.editable && (
          <>
            {opts.warnings.map((w, i) => <div key={i} className="rounded-lg border border-amber-800/60 bg-amber-950/30 px-3 py-2 text-xs text-amber-200">⚠ {w}</div>)}
            <table className="w-full text-sm">
              <thead><tr className="text-gray-500 text-xs border-b border-gray-800"><th className="text-left py-1.5">Item</th><th className="text-left">Size</th><th className="text-right">Qty</th></tr></thead>
              <tbody>
                {opts.lines.map(l => {
                  const lq = qty[l.line_id] ?? l.quantity;
                  return (
                    <tr key={l.line_id} className={`border-b border-gray-800/60 ${lq === 0 ? 'opacity-50' : ''}`}>
                      <td className="py-2 text-gray-200" title={customerWords(l)}>{titleEn(l)}
                        <div className="text-xs text-gray-500 font-mono">{l.product_code} · paid {money(l.paid_unit)}</div></td>
                      <td className="py-2">
                        {opts.can_swap ? (
                          <div className="space-y-1">
                            {chosen[l.line_id] ? (
                              <div className="text-xs text-blue-200">→ {chosen[l.line_id]}
                                <button onClick={() => { setTo(t => { const n = { ...t }; delete n[l.line_id]; return n; }); setChosen(c => { const n = { ...c }; delete n[l.line_id]; return n; }); setPreview(null); }}
                                  className="ml-2 underline text-gray-400 hover:text-white">undo</button></div>
                            ) : (
                              <select value={to[l.line_id] || l.variation_id} onFocus={() => loadFamily(l)} onMouseDown={() => loadFamily(l)}
                                onChange={e => { setTo(t => ({ ...t, [l.line_id]: e.target.value })); setPreview(null); }}
                                className="bg-gray-800 border border-gray-700 text-white text-sm rounded px-2 py-1 max-w-[12rem]">
                                <option value={l.variation_id}>{l.model_name || l.product_code} (now)</option>
                                {(fam[l.line_id] || []).filter(f => String(f.variation_id) !== String(l.variation_id)).map(f => (
                                  <option key={f.variation_id} value={f.variation_id}>{f.size || f.product_code} · stock {f.stock_qty ?? '?'}</option>
                                ))}
                              </select>
                            )}
                            <button onClick={() => { setSearchFor(searchFor === l.line_id ? null : l.line_id); setQ(''); setHits([]); }}
                              className="block text-[11px] text-gray-400 hover:text-white underline decoration-dotted">
                              {searchFor === l.line_id ? 'close' : 'other product…'}</button>
                            {searchFor === l.line_id && (
                              <div>
                                <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="code, barcode or name"
                                  className="w-full bg-gray-900 border border-gray-700 text-white text-xs rounded px-2 py-1" />
                                {hits.length > 0 && (
                                  <div className="mt-1 border border-gray-800 rounded max-h-40 overflow-auto">
                                    {hits.map(h => (
                                      <div key={h.variation_id} onClick={() => {
                                          setTo(t => ({ ...t, [l.line_id]: String(h.variation_id) }));
                                          setChosen(c => ({ ...c, [l.line_id]: `${titleEn(h) || h.product_code}${h.size ? ` · ${h.size}` : ''}` }));
                                          setSearchFor(null); setPreview(null);
                                        }}
                                        className="px-2 py-1 text-xs hover:bg-gray-800 cursor-pointer flex justify-between gap-2">
                                        <span className="truncate">{titleEn(h) || h.product_code} <span className="text-gray-500">{h.size || ''}</span></span>
                                        <span className={(h.stock_qty || 0) > 0 ? 'text-green-400' : 'text-amber-500'}>{h.stock_qty ?? '?'}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ) : <span className="text-xs text-gray-500" title={opts.cant_swap_why || ''}>{l.model_name || '—'}</span>}
                      </td>
                      <td className="py-2 text-right">
                        <input type="number" min={0} max={l.quantity} value={lq}
                          onChange={e => { setQty(s => ({ ...s, [l.line_id]: Math.max(0, Math.min(l.quantity, Number(e.target.value) || 0)) })); setPreview(null); }}
                          className="w-16 bg-gray-800 border border-gray-700 text-gray-100 text-sm rounded px-2 py-1 text-right" />
                        {lq === 0 && <div className="text-[10px] text-red-300">removed — refunded</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {!opts.can_swap && opts.cant_swap_why && <div className="text-xs text-gray-500">Size: {opts.cant_swap_why}.</div>}
            {preview && (
              <div className="rounded-lg border border-blue-900/60 bg-blue-950/30 px-3 py-2 text-sm space-y-1">
                <div className="font-semibold text-blue-200">The shop will change:</div>
                {(preview.changes || []).map((c: string, i: number) => <div key={i} className="text-gray-200">• {c}</div>)}
                <div className="text-gray-300">
                  {Math.abs(preview.money_change) < 0.005 ? 'Total unchanged.'
                    : preview.money_change < 0 ? `${money(-preview.money_change)} comes off — ${preview.refund || 'refunded to match'}.`
                    : `${money(preview.money_change)} more to pay — the shop shows the balance due.`}
                  {preview.staged?.total_after != null && <span className="text-gray-500"> Shop's own total after: {money(preview.staged.total_after)} (now {money(preview.total_before)}).</span>}
                </div>
                {(preview.stock || []).length > 0 && <div className="text-xs text-gray-400">Stock written by the WMS: {preview.stock.map((m: any) => `${m.article} ${m.delta > 0 ? '+' : ''}${m.delta}`).join(', ')}</div>}
                {opts.platform === 'shopify' && <div className="text-xs text-gray-500">Shopify moves its own stock: the old size back on sale, the new one taken.</div>}
              </div>
            )}
          </>
        )}
        {err && <div className="text-sm text-red-300">{err}</div>}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} disabled={busy} className="px-3 py-2 text-sm text-gray-400 hover:text-white">Cancel</button>
          {opts?.editable && !preview && (
            <button onClick={doPreview} disabled={busy || changes.length === 0}
              className="px-4 py-2 text-sm rounded-lg bg-gray-700 hover:bg-gray-600 text-white disabled:opacity-40">{busy ? 'Checking…' : 'Preview'}</button>
          )}
          {opts?.editable && preview && (
            <button onClick={doCommit} disabled={busy}
              className="px-4 py-2 text-sm rounded-lg bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-40">{busy ? 'Changing…' : 'Change the order in the shop'}</button>
          )}
        </div>
      </div>
    </div>
  );
}
